import React, { useEffect, useMemo, useState, useContext } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#ff002b";

const PdfCategoryList = () => {
  const { setLoading } = useContext(LoaderContext);
  const [tableLoading, setTableLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    let isMounted = true;
    const startTime = Date.now();
    setLoading(true);
    setTableLoading(true);

    try {
      const res = await axios.get(`${BASE_URL}/pdfcategory`);
      const data = Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      if (isMounted) setDataSource(data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      if (isMounted) setDataSource([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;

      setTimeout(() => {
        if (isMounted) {
          setLoading(false);
          setTableLoading(false);
        }
      }, remaining > 0 ? remaining : 0);
    }

    return () => {
      isMounted = false;
    };
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/pdfcategory/${id}`);
  };

  const handleAddNew = () => {
    navigate(`/dashboard/pdfcategory`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "Action",
        size: 40,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() =>
              handleEdit(row.original.pdfCategoryId)
            }
            sx={{
              color: themeColor,
              backgroundColor: "#ffe6e9",
              "&:hover": { backgroundColor: "#ffe6e9" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      {
        accessorKey: "pdfCategoryId",
        header: "ID",
        size: 60,
      },
      {
        accessorKey: "pdfCategoryName",
        header: "Category Name",
        size: 200,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
     
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ cell }) =>
          cell.getValue() === 1 ? (
            <Box
              sx={{
                px: 1.2,
                py: 0.3,
                bgcolor: "#e8f5e9",
                color: "#2e7d32",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              Active
            </Box>
          ) : (
            <Box
              sx={{
                px: 1.2,
                py: 0.3,
                bgcolor: "#ffebee",
                color: "#c62828",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              Inactive
            </Box>
          ),
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    if (!Array.isArray(dataSource)) return [];
    return [...dataSource]
      .reverse()
      .filter(
        (item) =>
          (item.isActive ?? 0) === statusFilter &&
          Object.values(item || {})
            .join(" ")
            .toLowerCase()
            .includes(globalFilter.toLowerCase())
      );
  }, [dataSource, globalFilter, statusFilter]);

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={4} sx={{ p: 2, borderRadius: 3 }}>

        {/* Header */}
        <Box display="flex" justifyContent="center" mt={1.5} mb={1.8}>
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
             sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              PDF Categories
            </Typography>
          </Box>
        </Box>

        <Divider  sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }} />

        {/* Filters */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={handleAddNew}
              sx={{
                borderRadius: 20,
                px: 2.2,
                backgroundColor: themeColor,
                textTransform: "none",
              }}
            >
              Add New
            </Button>

            {[{ label: "Active", value: 1 }, { label: "Inactive", value: 0 }].map(
              (btn) => (
                <Button
                  key={btn.value}
                  size="small"
                  variant={statusFilter === btn.value ? "contained" : "outlined"}
                  onClick={() => setStatusFilter(btn.value)}
                  sx={{
                    borderRadius: 20,
                    textTransform: "none",
                    backgroundColor:
                      statusFilter === btn.value ? themeColor : "#fff",
                    color:
                      statusFilter === btn.value ? "#fff" : themeColor,
                    borderColor: themeColor,
                  }}
                >
                  {btn.label}
                </Button>
              )
            )}
          </Stack>

          <TextField
            size="small"
            placeholder="Search categories..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: themeColor }} />
                </InputAdornment>
              ),
            }}
             sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                height: 34,
                "& fieldset": { borderColor: "#454444" },
                "&:hover fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 2px ${themeColor}22`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 3px ${themeColor}33`,
                },
              },
            }}
          />
        </Stack>
<MaterialReactTable
                        columns={columns}
                        data={filteredData}
                        state={{ isLoading: tableLoading }}
                        
                        enableTopToolbar={false}
                        enableColumnActions={false}
                        enableDensityToggle={false}
                        enableFullScreenToggle={false}
                        enableColumnFilters={false}
                        enableGlobalFilter={false}
                        enableHiding={false}
                      
                        muiTablePaperProps={{
                          sx: {
                            boxShadow: "none",
                            mt: 0.5,
                          },
                        }}
                      
                      
                        muiTableContainerProps={{
                          sx: {
                            maxHeight: { xs: "55vh", sm: "none" },
                            "&::-webkit-scrollbar": {
                              height: "6px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#cfcfcf",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              backgroundColor: "#b0b0b0",
                            },
                          },
                        }}
                      
                        muiTableHeadCellProps={{
                          sx: {
                            backgroundColor: "#ffe6e9",
                            fontWeight: 700,
                            fontSize: { xs: "12px", sm: "14px" },
                            py: { xs: 0.8, sm: 1.2 },
                            whiteSpace: "nowrap",
                          },
                        }}
                      
                        muiTableBodyRowProps={{
                          sx: {
                            "& td": {
                              fontSize: { xs: "11px", sm: "14px" },
                              py: { xs: 0.5, sm: 1 },
                              whiteSpace: "nowrap",
                            },
                            "&:hover": {
                              backgroundColor: "#fff8e1",
                            },
                          },
                        }}
                      
                        initialState={{
                          pagination: {
                            pageSize: 50,
                            pageIndex: 0,
                          },
                        }}
                      />
      </Paper>
    </Box>
  );
};

export default PdfCategoryList;
